/**
 * File: clientJobs.ts
 * Path: packages/config/src/clients/clientJobs.ts
 * Description: The work we do for a client: a website build, a monthly
 * retainer, a rescue job. Stored at clients/<clientId>/projects/<jobId> and
 * called a "job" in the admin, because "project" already means a public case
 * study in the portfolio. A job can point at the case study that shows it
 * (`portfolioId`); a case study is one view of a job, not the job itself.
 * Accounting invoices point at a client and, optionally, one of these.
 */

import { readMillis, readText } from "../leads/leads";

export const CLIENT_JOB_STATUSES = ["planned", "active", "on_hold", "done"] as const;
export type ClientJobStatus = (typeof CLIENT_JOB_STATUSES)[number];

export const CLIENT_JOB_STATUS_LABELS: Record<ClientJobStatus, string> = {
  planned: "Planned",
  active: "In progress",
  on_hold: "On hold",
  done: "Done",
};

/** Where a change request or an approval stands. */
export const JOB_ITEM_STATUSES = ["waiting", "approved", "declined"] as const;
export type JobItemStatus = (typeof JOB_ITEM_STATUSES)[number];
export const JOB_ITEM_STATUS_LABELS: Record<JobItemStatus, string> = {
  waiting: "Waiting for client",
  approved: "Approved",
  declined: "Declined",
};

/** Something the client asked for that was not in the agreement, and what it costs extra. */
export interface JobChange {
  id: string;
  title: string;
  /** Extra cost in whole cents; 0 when it is free. */
  extraCents: number;
  status: JobItemStatus;
  /** YYYY-MM-DD it was raised. */
  raisedOn: string;
}

/** Something the client has to sign off (a design, a draft, a launch). */
export interface JobApproval {
  id: string;
  title: string;
  status: JobItemStatus;
  /** YYYY-MM-DD we asked. */
  askedOn: string;
  /** YYYY-MM-DD they answered, or null while waiting. */
  answeredOn: string | null;
}

export const JOB_ITEMS_MAX = 50;
export const JOB_ITEM_TITLE_MAX = 200;
export const CLIENT_JOB_NAME_MAX = 200;
export const CLIENT_JOB_NOTES_MAX = 2000;

/** clients/<clientId>/projects */
export const clientJobsPath = (clientId: string): string => `clients/${clientId}/projects`;

export interface ClientJob {
  id: string;
  clientId: string;
  name: string;
  status: ClientJobStatus;
  /** YYYY-MM-DD the work started, when known. */
  startedOn: string | null;
  notes: string;
  /** The portfolio case study that shows this job, when there is one. */
  portfolioId: string | null;
  /** Change requests (scope that grew). */
  changes: JobChange[];
  /** Sign-offs asked of the client. */
  approvals: JobApproval[];
  createdAt: number | null;
  updatedAt: number | null;
}

/** What a person fills in. Changes and approvals are left out of a save that does not touch them. */
export type ClientJobDetails = Pick<ClientJob, "name" | "status" | "startedOn" | "notes" | "portfolioId"> &
  Partial<Pick<ClientJob, "changes" | "approvals">>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isClientJobStatus = (value: unknown): value is ClientJobStatus =>
  (CLIENT_JOB_STATUSES as readonly unknown[]).includes(value);

const isItemStatus = (value: unknown): value is JobItemStatus =>
  (JOB_ITEM_STATUSES as readonly unknown[]).includes(value);
const day = (value: unknown): string => {
  const text = readText(value);
  return ISO_DATE.test(text) ? text : "";
};
const list = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter((v): v is Record<string, unknown> => typeof v === "object" && v !== null).slice(0, JOB_ITEMS_MAX)
    : [];

const toJobChanges = (value: unknown): JobChange[] =>
  list(value).flatMap((row) => {
    const id = readText(row.id);
    const title = readText(row.title);
    const raisedOn = day(row.raisedOn);
    const extra = row.extraCents;
    if (!id || !title || !raisedOn) return [];
    return [
      {
        id,
        title,
        extraCents: typeof extra === "number" && Number.isInteger(extra) && extra >= 0 ? extra : 0,
        status: isItemStatus(row.status) ? row.status : "waiting",
        raisedOn,
      },
    ];
  });

const toJobApprovals = (value: unknown): JobApproval[] =>
  list(value).flatMap((row) => {
    const id = readText(row.id);
    const title = readText(row.title);
    const askedOn = day(row.askedOn);
    if (!id || !title || !askedOn) return [];
    return [
      {
        id,
        title,
        status: isItemStatus(row.status) ? row.status : "waiting",
        askedOn,
        answeredOn: day(row.answeredOn) || null,
      },
    ];
  });

/** Reads a stored job, filling gaps so the admin never renders undefined. */
export const toClientJob = (id: string, clientId: string, data: Record<string, unknown>): ClientJob => {
  const startedOn = readText(data.startedOn);
  return {
    id,
    clientId,
    name: readText(data.name) || "Untitled job",
    status: isClientJobStatus(data.status) ? data.status : "active",
    startedOn: ISO_DATE.test(startedOn) ? startedOn : null,
    notes: readText(data.notes),
    portfolioId: readText(data.portfolioId) || null,
    changes: toJobChanges(data.changes),
    approvals: toJobApprovals(data.approvals),
    createdAt: readMillis(data.createdAt),
    updatedAt: readMillis(data.updatedAt),
  };
};

/** The stored shape of a job's details: trimmed and clipped, nothing else. */
export const clientJobFields = (details: ClientJobDetails) => ({
  name: details.name.trim().slice(0, CLIENT_JOB_NAME_MAX),
  status: details.status,
  startedOn: details.startedOn && ISO_DATE.test(details.startedOn) ? details.startedOn : null,
  notes: details.notes.trim().slice(0, CLIENT_JOB_NOTES_MAX),
  portfolioId: details.portfolioId ? details.portfolioId : null,
  ...(details.changes
    ? {
        changes: details.changes.slice(0, JOB_ITEMS_MAX).map((c) => ({
          id: c.id,
          title: c.title.trim().slice(0, JOB_ITEM_TITLE_MAX),
          extraCents: Math.max(0, Math.round(c.extraCents)),
          status: c.status,
          raisedOn: c.raisedOn,
        })),
      }
    : {}),
  ...(details.approvals
    ? {
        approvals: details.approvals.slice(0, JOB_ITEMS_MAX).map((a) => ({
          id: a.id,
          title: a.title.trim().slice(0, JOB_ITEM_TITLE_MAX),
          status: a.status,
          askedOn: a.askedOn,
          answeredOn: a.answeredOn,
        })),
      }
    : {}),
});

/** Newest start first; jobs with no start date after the dated ones; stable by name. */
export const sortClientJobs = (jobs: readonly ClientJob[]): ClientJob[] =>
  [...jobs].sort((a, b) => {
    if (a.startedOn !== b.startedOn) {
      if (a.startedOn === null) return 1;
      if (b.startedOn === null) return -1;
      return a.startedOn < b.startedOn ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });
